import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Box, Button, CircularProgress } from "@mui/material";
import { InputGroup, ScrollableContent } from "../Styles/InputStyles";

const OnboardingSettingsModal = ({
  onClose,
  settingsData,
  editOnboardSettingsMutate,
  isonboardSettingsLoading,
  property,
  propertyList,
  fetchOnboardingSettingsLoading,
  isDataLoaded
}) => {
  const [settings, setSettings] = useState({
    aadhaarOtpVerification: settingsData?.aadhaarOtpVerification === 1,
    policeVerification: settingsData?.policeVerification === 1,
    onlinePayment: settingsData?.onlinePayment === 1,
    rentAgreement: settingsData?.rentAgreement === 1,
    rentalBond: settingsData?.rentalBond === 1,
  });

  const [propertySelectOpen, setPropertySelectOpen] = useState(false);
  const [tempSelectedProps, setTempSelectedProps] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const allProperties = propertyList || [];

  useEffect(() => {
    if (property?.id === 0) {
      const allPropertyIds = allProperties.map(prop => prop.id);
      setSelectedProperties(allPropertyIds);
      setTempSelectedProps(allPropertyIds);
    } else {
      setSelectedProperties(property?.id ? [property.id] : []);
      setTempSelectedProps(property?.id ? [property.id] : []);
    }
  }, [property, allProperties]);

  const toggleSetting = (key, isEnabled) => {
    const updatedState = { ...settings, [key]: !settings[key] };
    setSettings(updatedState);

    let propertiesToSend = [];

    if (property?.id === 0) {
      if (selectedProperties.includes(0)) {
        propertiesToSend = allProperties.map(prop => prop.id);
      } else {
        propertiesToSend = selectedProperties;
      }
    } else {
      propertiesToSend = [property.id];
    }

    const payload = {
      type: key,
      isEnabled: isEnabled ? 1 : 0,
      selectedProperties: propertiesToSend
    };

    editOnboardSettingsMutate(payload);
  };

  const handlePropertySelectOpen = () => {
    setTempSelectedProps(selectedProperties || []);
    setPropertySelectOpen(true);
  };

  const handlePropertySelectClose = (confirmed) => {
    if (confirmed) {
      setSelectedProperties(tempSelectedProps);
    }
    setPropertySelectOpen(false);
  };

  const togglePropertySelection = (propId) => {
    if (propId === 0) {
      if (tempSelectedProps.includes(0)) {
        setTempSelectedProps([]);
      } else {
        setTempSelectedProps([0]);
      }
    } else {
      if (tempSelectedProps.includes(0)) {
        setTempSelectedProps([propId]);
      } else {
        const newSelected = tempSelectedProps.includes(propId)
          ? tempSelectedProps.filter((id) => id !== propId)
          : [...tempSelectedProps, propId];
        setTempSelectedProps(newSelected);
      }
    }
  };

  const isAllPropertiesSelected = () => {
    return tempSelectedProps.includes(0);
  };

  const renderSelectedProperties = (selected = []) => {
    if (!selected.length) return "Select Properties *";

    if (selected.includes(0) || selected.length === allProperties.length) {
      return "All Properties";
    }

    const selectedPropertyNames = allProperties
      .filter((prop) => selected.includes(prop.id))
      .map((prop) => prop.name);

    if (!selectedPropertyNames.length) return "Select Properties *";

    const maxToShow = 2;
    const namesToShow = selectedPropertyNames.slice(0, maxToShow);
    const remainingCount = selectedPropertyNames.length - maxToShow;

    return (
      <PropertyDisplayContainer>
        {namesToShow.join(", ")}
        {remainingCount > 0 && (
          <RemainingCount>+{remainingCount}</RemainingCount>
        )}
      </PropertyDisplayContainer>
    );
  };


  return (
    <div className="bg-white w-full h-full flex flex-col">
      <Header>
        <HeaderTitle>Onboarding Settings</HeaderTitle>
        <CloseButton onClick={onClose}>
          <MdClose color="white" size={24} />
        </CloseButton>
      </Header>
      {fetchOnboardingSettingsLoading || !isDataLoaded ? (
        <div className="flex items-center justify-center h-full">
          <CircularProgress />

        </div>
      ) : (
        // Your existing modal content
        <>
          {/* <ScrollableContent className="px-4 flex flex-col justify-between pb-4 relative max-h-[calc(100dvh-100px)]"> */}
          <ContentContainer>
            <ContentWrapper>
              <Section>
                {property?.id === 0 && (
                  <PropertySection>
                    <InputGroup>
                      <Select
                        id="properties"
                        name="properties"
                        open={propertySelectOpen}
                        onOpen={handlePropertySelectOpen}
                        onClose={() => { }}
                        value={selectedProperties}
                        renderValue={() => renderSelectedProperties(selectedProperties)}
                        sx={{
                          height: "40px",
                          backgroundColor: selectedProperties.length === 0 ? '#fef2f2' : '#f9fafb',

                        }}
                        className="px-2"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              borderRadius: '12px',
                              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            },
                          },
                          disableAutoFocusItem: true,
                          onClose: (event, reason) => {
                            if (reason !== 'backdropClick') {
                              setPropertySelectOpen(false);
                            }
                          }
                        }}
                      >
                        <Box sx={{ p: 1 }}>
                          {allProperties.map((prop) => (
                            <MenuItem
                              key={prop.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePropertySelection(prop.id);
                              }}
                              disabled={prop.id !== 0 && isAllPropertiesSelected()}
                              sx={{
                                borderRadius: '6px',
                                margin: '2px 0',
                                '&:hover': {
                                  backgroundColor: '#f3f4f6',
                                }
                              }}
                            >
                              <Checkbox
                                checked={tempSelectedProps.includes(prop.id)}
                                disabled={prop.id !== 0 && isAllPropertiesSelected()}
                                sx={{
                                  color: '#6b7280',
                                  '&.Mui-checked': {
                                    color: '#3b82f6',
                                  }
                                }}
                              />
                              <ListItemText
                                primary={prop.name}
                                sx={{
                                  opacity: prop.id !== 0 && isAllPropertiesSelected() ? 0.5 : 1,
                                  '& .MuiListItemText-primary': {
                                    fontWeight: 500,
                                    color: '#374151'
                                  }
                                }}
                              />
                            </MenuItem>
                          ))}

                          <ActionButtonsContainer>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePropertySelectClose(false)}
                              sx={{
                                borderColor: '#d1d5db',
                                color: '#6b7280',
                                '&:hover': {
                                  borderColor: '#9ca3af',
                                  backgroundColor: '#f9fafb',
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => handlePropertySelectClose(true)}
                              disabled={tempSelectedProps.length === 0}
                              sx={{
                                backgroundColor: '#3b82f6',
                                '&:hover': {
                                  backgroundColor: '#2563eb',
                                },
                                '&:disabled': {
                                  backgroundColor: '#d1d5db',
                                }
                              }}
                            >
                              Confirm
                            </Button>
                          </ActionButtonsContainer>
                        </Box>
                      </Select>
                      {selectedProperties.length === 0 && (
                        <ErrorText>At least one property is required</ErrorText>
                      )}
                    </InputGroup>
                  </PropertySection>
                )}

                <SettingsGrid>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Digi Locker Aadhaar Verification</SettingTitle>
                        <SettingDescription>
                          Verify tenant identity securely and streamline the onboarding process.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.aadhaarOtpVerification}
                        onChange={() => toggleSetting("aadhaarOtpVerification", !settings.aadhaarOtpVerification)}
                        disabled={isonboardSettingsLoading}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Police Verification</SettingTitle>
                        <SettingDescription>
                          Enable this to conduct a thorough police verification process.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.policeVerification}
                        onChange={() => toggleSetting("policeVerification", !settings.policeVerification)}
                        disabled={isonboardSettingsLoading}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Online Payment</SettingTitle>
                        <SettingDescription>
                          Allow tenants to make secure and hassle-free online payments.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.onlinePayment}
                        onChange={() => toggleSetting("onlinePayment", !settings.onlinePayment)}
                        disabled={isonboardSettingsLoading}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Generate Rent Agreement</SettingTitle>
                        <SettingDescription>
                          Simplify the rental process with a digital rent agreement.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.rentAgreement}
                        onChange={() => toggleSetting("rentAgreement", !settings.rentAgreement)}
                        disabled={isonboardSettingsLoading}
                      />
                    </SettingContent>
                  </SettingCard>

                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Enable Rental Bond</SettingTitle>
                        <SettingDescription>
                          Enhance security with Rental Bond for a hassle-free leasing experience.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.rentalBond}
                        onChange={() => toggleSetting("rentalBond", !settings.rentalBond)}
                        disabled={isonboardSettingsLoading}
                      />
                    </SettingContent>
                  </SettingCard>
                </SettingsGrid>
              </Section>

            </ContentWrapper>
          </ContentContainer>
        </>)}
    </div>
  );
};
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const PropertySection = tw.div`bg-gray-50 rounded-xl p-4 border border-gray-200 mb-4`;
const PropertyDisplayContainer = tw.div`flex items-center gap-1 truncate`;
const RemainingCount = tw.span`text-gray-500 ml-1 font-medium`;
const ErrorText = tw.div`text-red-600 text-sm mt-2 font-medium`;
const ActionButtonsContainer = tw(Box)`sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex justify-end gap-2 z-10 mt-2`;
const SettingsGrid = tw.div`space-y-4`;
const SettingCard = tw.div`bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200`;
const SettingContent = tw.div`flex items-start justify-between space-x-6 p-5`;
const SettingHeader = tw.div`flex-1`;
const SettingTitle = tw.h3`font-semibold text-gray-800 mb-2 text-base`;
const SettingDescription = tw.p`text-sm text-gray-600 leading-relaxed`;

const Toggle = ({ checked, onChange, disabled }) => (
  <ToggleLabel disabled={disabled}>
    <ToggleInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <ToggleSlider checked={checked} disabled={disabled}>
      <ToggleThumb checked={checked} />
    </ToggleSlider>
  </ToggleLabel>
);
const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-blue-600' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;

export default OnboardingSettingsModal;